import { Button } from '@mui/material';
import { Upload } from 'lucide-react';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import SearchInput from '~/components/Inputs/Search';
import { ListToolbarFrame } from './shared';

type PatientListToolbarProps = {
    /** Patients the current query matches, once known; undefined while loading or failed. */
    totalCount: number | undefined;
    /** Whether the search term or a filter narrowed the query the total counts against. */
    isNarrowed: boolean;
    searchInput: string;
    onSearchInputChange: (value: string) => void;
    /** True while the list is re-fetched for a changed query; the count shown is then the previous one. */
    isFetching: boolean;
    onImport: () => void;
    /** The list's views, beside the title. */
    tabs?: ReactNode;
    /** The rows of filters. */
    children: ReactNode;
};

/**
 * The block above the list: the title, the views and, while the list is
 * narrowed, the match count, with search and import at the end of the line;
 * then the rows of filters. Sorting lives in the column headers.
 */
export default function PatientListToolbar({
    totalCount,
    isNarrowed,
    searchInput,
    onSearchInputChange,
    isFetching,
    onImport,
    tabs,
    children,
}: PatientListToolbarProps) {
    const intl = useIntl();
    return (
        <ListToolbarFrame
            tabs={tabs}
            count={
                isNarrowed &&
                totalCount !== undefined && (
                    <FormattedMessage
                        id="patientList.subtitle.matchCount"
                        values={{ totalCount }}
                    />
                )
            }
            isFetching={isFetching}
            actions={
                <>
                    <SearchInput
                        value={searchInput}
                        onChange={onSearchInputChange}
                        placeholder={intl.formatMessage({
                            id: 'patientList.search.placeholder',
                        })}
                        clearLabel={intl.formatMessage({
                            id: 'patientList.search.clear',
                        })}
                        inputProps={{
                            'aria-label': intl.formatMessage({
                                id: 'patientList.search.ariaLabel',
                            }),
                        }}
                        sx={{ width: 300 }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<Upload size={14} />}
                        onClick={onImport}
                    >
                        <FormattedMessage id="patientList.toolbar.import" />
                    </Button>
                </>
            }
        >
            {children}
        </ListToolbarFrame>
    );
}
